import { rtkApi } from '@/shared/api/rtkApi';

export type PushProviderName = 'fcm';

export interface PushProviderState {
  appScope: string;
  provider: PushProviderName;
  configured: boolean;
  enabled: boolean;
  projectId: string | null;
  clientEmail: string | null;
  fingerprint: string | null;
  updatedAt: string | null;
  updatedBy: { id: number; name: string | null } | null;
}

export interface PushProvidersResponse {
  providers: PushProviderState[];
}

export interface FcmCheckResponse extends PushProviderState {
  authenticated: boolean;
  checkReason: string | null;
}

export type PushDeliveryStatus = 'success' | 'retryable' | 'invalid_token' | 'permanent_failure';

export interface PushDeliveryResult {
  provider: PushProviderName;
  status: PushDeliveryStatus;
  reason: string | null;
}

export interface PushTestResponse {
  installationId: string;
  sent: boolean;
  results: PushDeliveryResult[];
}

export interface TestPushArgs {
  installationId: string;
  title?: string;
  body: string;
}

const pushProviderApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getPushProviders: build.query<PushProvidersResponse, void>({
      query: () => '/v1/superadmin/push/providers',
      providesTags: [{ type: 'PushProviders', id: 'LIST' }],
    }),
    getPushProvider: build.query<PushProviderState, PushProviderName>({
      query: (provider) => `/v1/superadmin/push/providers/${provider}`,
      providesTags: (_result, _error, provider) => [{ type: 'PushProviders', id: provider }],
    }),
    uploadPushProviderCredentials: build.mutation<PushProviderState, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('serviceAccount', file);
        return {
          url: '/v1/superadmin/push/providers/fcm/credentials',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [
        { type: 'PushProviders', id: 'LIST' },
        { type: 'PushProviders', id: 'fcm' },
      ],
    }),
    checkPushProviderCredentials: build.mutation<FcmCheckResponse, void>({
      query: () => ({
        url: '/v1/superadmin/push/providers/fcm/check',
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'PushProviders', id: 'LIST' },
        { type: 'PushProviders', id: 'fcm' },
      ],
    }),
    setPushProviderEnabled: build.mutation<PushProviderState, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: '/v1/superadmin/push/providers/fcm',
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: [
        { type: 'PushProviders', id: 'LIST' },
        { type: 'PushProviders', id: 'fcm' },
      ],
    }),
    deletePushProviderCredentials: build.mutation<void, void>({
      query: () => ({
        url: '/v1/superadmin/push/providers/fcm/credentials',
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'PushProviders', id: 'LIST' },
        { type: 'PushProviders', id: 'fcm' },
      ],
    }),
    sendPushProviderTest: build.mutation<PushTestResponse, TestPushArgs>({
      query: ({ installationId, title, body }) => ({
        url: '/v1/superadmin/push/test',
        method: 'POST',
        body: { installationId, ...(title ? { title } : {}), body },
      }),
    }),
  }),
});

export const {
  useGetPushProvidersQuery,
  useGetPushProviderQuery,
  useUploadPushProviderCredentialsMutation,
  useCheckPushProviderCredentialsMutation,
  useSetPushProviderEnabledMutation,
  useDeletePushProviderCredentialsMutation,
  useSendPushProviderTestMutation,
} = pushProviderApi;
